<template>
  <v-container fluid v-if="user.can('view_rootentity')">
    <v-alert v-if="noCoolerProducts && noFreezerProducts" color="danger">
      {{ $t('no_products_contact_support') }}
    </v-alert>
    <h2>{{ $tc('upload_route') }}</h2>
    <v-form @submit.prevent="onSubmit" autocomplete="off" name="lastpass-disable-search">
      <div class="mb-2">
        <route-plan-picker
          v-if="useRoutePlans"
          v-model="route.route_plan"
          :customerId="customerContext.id"
          :error-messages="routePlanErrors"
          @no-route-plans="disableRoutePlans"/>
        <route-name-picker
          v-else
          v-model="route.name"
          :error-messages="routeNameErrors"
          :routeNameRules="routeNameRules"/>
        <vehicle-picker
          v-model="route.vehicle"
          :customer="customerContext"
          :error-messages="vehicleErrors"/>
      </div>
      <h4>{{ $tc('route_product', 2) }}</h4>
      <v-row>
        <v-col>
          <v-card>
            <v-card-title>
              {{ $tc('cooler') }}
            </v-card-title>
            <v-card-text>
              <h6 class="d-flex align-center">
                {{ $tc('air_temperature') }}
                <info-modal class="ml-1">
                  <v-card-title>
                    {{ $t('air_temperature') }}
                  </v-card-title>
                  <v-card-text>
                    <div v-html="$t('route_upload_ui.sensor_data_upload_help')"></div>
                  </v-card-text>
                </info-modal>
              </h6>
              <sensor-file-upload
                ref="cooler-file-upload"
                v-model="sensorData.cooler"
                :errors="coolerSensorErrors"
                :customerId="this.customerContext.id"/>
              <route-product-selection
                v-show="sensorData.cooler"
                ref="cooler-route-products"
                class="mt-2"
                zone="cooler"
                populateWithDefaults
                :label="$tc('cooler_products')"
                v-model="products.cooler"
                :route-customer="this.customerContext"
                @no-products="noCoolerProducts = true"/>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col>
          <v-card>
            <v-card-title>
              {{ $tc('freezer') }}
            </v-card-title>
            <v-card-text>
              <h6 class="d-flex align-center">
                {{ $tc('air_temperature') }}
                <info-modal class="ml-1">
                  <v-card-title>
                    {{ $t('air_temperature') }}
                  </v-card-title>
                  <v-card-text>
                    <div v-html="$t('route_upload_ui.sensor_data_upload_help')"></div>
                  </v-card-text>
                </info-modal>
              </h6>
              <sensor-file-upload
                ref="freezer-file-upload"
                v-model="sensorData.freezer"
                :errors="freezerSensorErrors"
                :customerId="this.customerContext.id"/>
              <route-product-selection
                v-show="sensorData.freezer"
                ref="freezer-route-products"
                class="mt-2"
                zone="freezer"
                populateWithDefaults
                :label="$tc('freezer_products')"
                v-model="products.freezer"
                :route-customer="this.customerContext"
                @no-products="noFreezerProducts = true"/>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <div class="d-flex mt-4">
        <v-spacer/>
        <v-btn color="primary" type="submit" :loading="isSaving">
          {{ $tc('create_route') }}
        </v-btn>
      </div>
    </v-form>
  </v-container>
</template>

<script>
import Vue from 'vue'
import RoutePlanPicker from '@/components/route/form/RoutePlanPicker'
import RouteNamePicker from '@/components/route/form/RouteNamePicker'
import VehiclePicker from '@/components/route/form/VehiclePicker'
import SensorFileUpload from '@/components/route/form/SensorFileUpload'
import RouteProductSelection from '@/components/route/form/RouteProductSelection'
import InfoModal from '@/components/shared/InfoModal'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import { min, minBy, max, maxBy, isEmpty, each } from 'lodash'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'RouteUpload',
  components: {
    RouteNamePicker,
    RoutePlanPicker,
    VehiclePicker,
    SensorFileUpload,
    RouteProductSelection,
    InfoModal
  },
  mixins: [
    CurrentUserMixin,
    ValidationMixin,
    ApiUtilitiesMixin
  ],
  validations: {
    route: {
      vehicle: {
        required
      },
      route_plan: {
        required: function (value) {
          return this.useRoutePlans ? value != null : true
        }
      },
      name: {
        required: function (value) {
          return !this.useRoutePlans ? value != null : true
        },
        maxLength: function (value) {
          if (!this.useRoutePlans && value != null) {
            return value.length <= 30
          }
          return true
        }
      }
    },
    sensorData: {
      cooler: {
        required
      },
      freezer: {
        required
      }
    }
  },
  data () {
    return {
      route: this.$orm('Route').createRecord(),
      useRoutePlans: false,
      routeNameRules: null,
      isSaving: false,
      noCoolerProducts: false,
      noFreezerProducts: false,
      products: {
        cooler: [],
        freezer: []
      },
      sensorData: {
        cooler: null,
        freezer: null
      }
    }
  },
  computed: {
    /**
     * Determine the route start by finding the earliest time from the air temperature data
     * @returns {*}
     */
    routeStart () {
      const coolerStart = minBy(this.sensorData.cooler, 'timestamp')
      const freezerStart = minBy(this.sensorData.freezer, 'timestamp')
      return min([coolerStart.timestamp, freezerStart.timestamp])
    },
    /**
     * Determine the route end by finding the latest time from the air temperature data
     * @returns {*}
     */
    routeEnd () {
      const coolerEnd = maxBy(this.sensorData.cooler, 'timestamp')
      const freezerEnd = maxBy(this.sensorData.freezer, 'timestamp')
      return max([coolerEnd.timestamp, freezerEnd.timestamp])
    },
    vehicleErrors () {
      return this.validationErrors([{
        prop: this.$v.route.vehicle,
        rule: 'required',
        message: this.$t('vehicle_selection_required')
      }])
    },
    routePlanErrors () {
      return this.validationErrors([{
        prop: this.$v.route.route_plan,
        rule: 'required',
        message: this.$t('route_selection_required')
      }])
    },
    routeNameErrors () {
      return this.validationErrors([{
        prop: this.$v.route.name,
        rule: 'required',
        message: this.$t('route_selection_required')
      }, {
        prop: this.$v.route.name,
        rule: 'maxLength',
        message: this.$t('max_length_error', {
          entity: this.$t('route_name'),
          max: 30
        })
      }])
    },
    coolerSensorErrors () {
      return this.validationErrors([{
        prop: this.$v.sensorData.cooler,
        rule: 'required',
        message: this.$tc('route_upload_ui.cooler_sensor_data_required')
      }])
    },
    freezerSensorErrors () {
      return this.validationErrors([{
        prop: this.$v.sensorData.freezer,
        rule: 'required',
        message: this.$tc('route_upload_ui.freezer_sensor_data_required')
      }])
    }
  },
  methods: {
    /**
     * Initialize the component.
     */
    initialize () {
      this.route = this.$orm('Route').createRecord()
      this.useRoutePlans = this.customerContext.getSetting('routePlanRouteCreate', false)
      this.routeNameRules = this.customerContext.getSetting('routeNames')
    },
    /**
     * Reset the component back to initial state
     */
    reset () {
      this.$v.$reset()
      this.sensorData.cooler = null
      this.sensorData.freezer = null
      this.products.cooler = []
      this.products.freezer = []

      // Wait a tick for changed values to be reflected in child components
      Vue.nextTick(() => {
        this.$refs['cooler-file-upload'].reset()
        this.$refs['freezer-file-upload'].reset()
        this.$refs['cooler-route-products'].reset()
        this.$refs['freezer-route-products'].reset()
      })
    },
    /**
     * Update the component when the customer context changes.
     */
    customerContextChangeHandler () {
      this.initialize()
      this.reset()
    },
    /**
     * When route plans are enabled but the route plan picker can't find any
     * route plans, fall back to route name picker
     */
    disableRoutePlans () {
      this.useRoutePlans = false
    },
    /**
     * Submit the form
     */
    async onSubmit () {
      if (!this.validate()) {
        return false
      }
      this.isSaving = true
      try {
        if (await this.vehicleDataExists()) {
          const message = this.$tc('route_upload_ui.vehicle_data_already_exists_prompt')
          if (!await this.$bvModal.msgBoxConfirm(message)) {
            return false
          }
        }
        await this.uploadVehicleData()
        const savedRoute = await this.createRoute()
        this.$router.push('/route/' + savedRoute.id, () => {
          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            this.$t('route_created', { route_name: savedRoute.name }))
        })
      } catch (error) {
        this.renderErrorModal(error)
        throw error
      } finally {
        this.isSaving = false
      }
    },
    /**
     * Check to see if vehicle data already exists in the route window defined by the sensor data
     * @returns {Promise<boolean>}
     */
    async vehicleDataExists () {
      const response = await this.$orm('VehicleData').findAll({
        vehicle_id: this.route.vehicle.id,
        timestamp_after: this.routeStart,
        timestamp_before: this.routeEnd
      })
      return !isEmpty(response.getItems())
    },
    /**
     * Upload the cooler/freezer air temperature data as vehicle data before creating the Route
     * @returns {Promise<void>}
     */
    uploadVehicleData () {
      // Prep the data for upload by attaching the sensor value and combining into one array
      const uploadData = []
      each(['cooler', 'freezer'], sensor => {
        each(this.sensorData[sensor], item => {
          uploadData.push({
            vehicle_id: this.route.vehicle.id,
            timestamp: item.timestamp,
            sensor: sensor,
            value: item.value
          })
        })
      })
      return this.$httpAdapter({
        url: this.$orm('VehicleData').endpoint + '/bulk-upsert',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(uploadData)
      })
    },
    /**
     * POST the route to the API.
     * @returns {Promise<any>}
     */
    async createRoute () {
      // Prepare the selected route products to be submitted as JSON
      this.route.route_products = this.products.cooler.concat(this.products.freezer)
      each(this.route.route_products, (routeProduct) => {
        routeProduct.temperature_scale = this.customerContext.tempFormat()
      })

      // If we're using route plans, use the route plan's name as the route's name.
      if (this.useRoutePlans) {
        this.route.name = this.route.route_plan.route_number
      }
      // As with on the route form, we're submitting the route products along with the route,
      // and JS-Data doesn't really handle this in the non-standard way that we're doing it,
      // so we'll use $httpAdapter to POST it manually.
      const json = this.route.toJSON({ with: ['route_products'] })
      json.start = this.routeStart
      json.end = this.routeEnd

      const response = await this.$httpAdapter({
        url: this.$orm('Route').endpoint,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(json)
      })
      return response.data
    },
    /**
     * Validate the form
     * @returns {*|boolean}
     */
    validate () {
      this.$v.$touch()
      let coolerRouteProductsValid = false
      let freezerRouteProductsValid = false

      coolerRouteProductsValid = this.$refs['cooler-route-products'].validate()
      freezerRouteProductsValid = this.$refs['freezer-route-products'].validate()

      return (!this.$v.$invalid) && coolerRouteProductsValid && freezerRouteProductsValid
    }
  },
  mounted () {
    this.initialize()
  }
}
</script>
